import type { CompanyDataResponse } from '../@types/Company';
import type { FilterableCompany } from './companyListFilters';
import { getDjActivities } from './companyListFilters';

/** GET /api/admin/card-holders: one row of values per holder, in the order of `columns`, flags as 0/1. */
export interface CardHolderListResponse {
  dataDate: string | null;
  columns: string[];
  rows: Array<Array<string | number | null>>;
}

export interface CardHolderListItem extends FilterableCompany {
  id: number;
  postalCode: string | null;
  city: string | null;
  /** Valid ADC attestations under the company's card, by status of the collaborator. */
  nbAgentsCo: number;
  nbSalaries: number;
}

/** GET /api/admin/card-holders/{id}: the company details keys, plus what only the CCI file holds. */
export type CardHolderDetails = Partial<CompanyDataResponse> & {
  id: number;
  name: string;
  formeJuridique: string | null;
  cciLibelle: string | null;
  banqueCompteSequestre: string | null;
};

const FLAG_COLUMNS = [
  'fnaim', 'unis', 'snpi',
  'syndic', 'gesLoc', 'transa', 'loc',
  'syndicPappers', 'gestionImmobilierePappers', 'transactionPappers',
] as const;

export const toCardHolderListItems = ({ columns, rows }: CardHolderListResponse): CardHolderListItem[] => {
  const index = new Map(columns.map((column, position) => [column, position]));
  const position = (column: string): number => index.get(column) ?? -1;
  const flagPositions = FLAG_COLUMNS.map((flag) => [flag, position(flag)] as const);
  const [id, name, siren, postalCode, city, carte, dateFinCarte, nbLotsHab, nbLotsHabBurCom, nbAgentsCo, nbSalaries] = [
    'id', 'name', 'siren', 'postalCode', 'city', 'carte', 'dateFinCarte', 'nbLotsHab', 'nbLotsHabBurCom', 'nbAgentsCo', 'nbSalaries',
  ].map(position);

  const text = (row: CardHolderListResponse['rows'][number], at: number): string | null => {
    const value = at < 0 ? null : row[at];
    return value === null || value === undefined ? null : String(value);
  };
  const count = (row: CardHolderListResponse['rows'][number], at: number): number => {
    const value = Number(at < 0 ? 0 : row[at]);
    return Number.isNaN(value) ? 0 : value;
  };

  return rows.map((row) => {
    const details: CardHolderListItem['details'] = {};
    for (const [flag, at] of flagPositions) {
      details[flag] = at >= 0 && row[at] === 1;
    }
    details.carte = text(row, carte);
    details.dateFinCarte = text(row, dateFinCarte);
    details.nbLotsHab = count(row, nbLotsHab);
    details.nbLotsHabBurCom = count(row, nbLotsHabBurCom);

    return {
      id: count(row, id),
      name: text(row, name) ?? '',
      siren: text(row, siren),
      postalCode: text(row, postalCode),
      city: text(row, city),
      details,
      activities: getDjActivities(details),
      nbLotsHab: details.nbLotsHab,
      nbLotsTotal: details.nbLotsHabBurCom,
      nbAgentsCo: count(row, nbAgentsCo),
      nbSalaries: count(row, nbSalaries),
    };
  });
};

export const CARD_HOLDER_EXPORT_HEADERS = [
  'NOM', 'SIREN', 'Code postal', 'Ville',
  'Carte', 'Date fin carte',
  'Membre FNAIM', 'Membre UNIS', 'Membre SNPI',
  'Activité Syndic', 'Activité Gestion locative', 'Activité Transaction', 'Activité Location',
  'Syndic CARTE', 'Gestion Immobilière CARTE', 'Transaction CARTE',
  'NB LOTS HAB', 'NB LOTS HAB / BUR / COM',
  'NB AGENTS CO', 'NB SALARIÉS',
];

export const CARD_HOLDER_EXPORT_WIDTHS = [40, 15, 12, 25, 25, 15, 15, 15, 15, 15, 20, 20, 15, 15, 20, 20, 15, 20, 15, 15];

const yesNo = (value: unknown): string => (value ? 'Oui' : 'Non');

/** 2027-03-31 → 31/03/2027, without going through Date (no time zone shift). */
export const formatIsoDay = (isoDate: string | null | undefined): string => {
  const match = isoDate?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

export const toCardHolderExportRow = (item: CardHolderListItem): Array<string | number> => [
  item.name,
  item.siren ?? '',
  item.postalCode ?? '',
  item.city ?? '',
  item.details.carte ?? '',
  formatIsoDay(item.details.dateFinCarte),
  yesNo(item.details.fnaim),
  yesNo(item.details.unis),
  yesNo(item.details.snpi),
  yesNo(item.details.syndic),
  yesNo(item.details.gesLoc),
  yesNo(item.details.transa),
  yesNo(item.details.loc),
  yesNo(item.details.syndicPappers),
  yesNo(item.details.gestionImmobilierePappers),
  yesNo(item.details.transactionPappers),
  item.nbLotsHab,
  item.nbLotsTotal,
  item.nbAgentsCo,
  item.nbSalaries,
];
